import {
  Component,
  OnDestroy,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import * as am5 from '@amcharts/amcharts5';
import { EtatService } from '../../../Services/etat';
import { ParcService, SourceEntry } from '../../../Services/parc';
import {
  Installation as InstallationService,
  ModeAffichageGraphique,
} from '../../../Services/installation';
import { ChartHostOverlayComponent } from '../../../Composants/chart-host-overlay/chart-host-overlay';
import {
  ChartPeriodSelectorComponent,
  PeriodeChangee,
} from '../../../Composants/chart-period-selector/chart-period-selector';
import { ReferentChartPoint, createReferentChart } from './debug-referents-parc-chart';

const MODES_VALIDES: ModeAffichageGraphique[] = ['jour', 'mois', 'annee'];
const REFERENTS_VALIDES = ['ref_production', 'ref_ensoleillement'] as const;

function fmtDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

@Component({
  selector: 'app-debug-referents-parc',
  imports: [FormsModule, ChartPeriodSelectorComponent, ChartHostOverlayComponent],
  templateUrl: './debug-referents-parc.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './debug-referents-parc.css',
})
export class DebugReferentsParc implements OnDestroy {
  private etat = inject(EtatService);
  private parcService = inject(ParcService);
  private installationService = inject(InstallationService);
  private platformId = inject(PLATFORM_ID);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  parcs = this.etat.parcs;
  parcIdSelectionne = signal<number | null>(this.etat.parcs().find((p) => p.global)?.id ?? null);
  typeReferent = signal<'ref_production' | 'ref_ensoleillement'>('ref_production');
  mode = signal<ModeAffichageGraphique>('jour');
  dateFin = signal<Date>(new Date());

  chargement = signal(false);
  erreur = signal<string | null>(null);
  afficherGraphique = signal(false);
  chargementGraphique = signal(false);

  tableauColonnes = signal<{ label: string; ts: number }[]>([]);
  tableauLignes = signal<{ id: string; label: string; valeurs: (number | null)[] }[]>([]);
  sortEtat = signal<{ colonne: number | 'source'; direction: 'asc' | 'desc' } | null>(null);
  plageSelectionnee = signal<{ debut: number; fin: number } | null>(null);

  private indicesVisibles = computed(() => {
    const colonnes = this.tableauColonnes();
    const plage = this.plageSelectionnee();
    if (!plage) return colonnes.map((_, i) => i);
    return colonnes.reduce<number[]>((acc, col, i) => {
      if (col.ts >= plage.debut && col.ts <= plage.fin) acc.push(i);
      return acc;
    }, []);
  });

  colonnesVisibles = computed(() =>
    this.indicesVisibles().map((i) => ({ ...this.tableauColonnes()[i], indexOriginal: i })),
  );

  lignesTriees = computed(() => {
    const lignes = this.tableauLignes();
    const sort = this.sortEtat();
    if (!sort) return lignes;
    return [...lignes].sort((a, b) => {
      let cmp: number;
      if (sort.colonne === 'source') {
        cmp = a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' });
      } else {
        const va = a.valeurs[sort.colonne as number] ?? -Infinity;
        const vb = b.valeurs[sort.colonne as number] ?? -Infinity;
        cmp = va < vb ? -1 : va > vb ? 1 : 0;
      }
      return sort.direction === 'asc' ? cmp : -cmp;
    });
  });

  lignesFiltrees = computed(() =>
    this.lignesTriees().map((ligne) => ({
      ...ligne,
      valeurs: this.indicesVisibles().map((i) => ligne.valeurs[i] ?? null),
    })),
  );

  ligneRef = signal<(number | null)[]>([]);
  ligneSomme = signal<(number | null)[]>([]);

  ligneRefVisible = computed(() => this.indicesVisibles().map((i) => this.ligneRef()[i] ?? null));
  ligneSommeVisible = computed(() =>
    this.indicesVisibles().map((i) => this.ligneSomme()[i] ?? null),
  );

  private chartRoot?: am5.Root;
  private chartHost?: HTMLDivElement;
  private chartData: ReferentChartPoint[] = [];
  seriesLabel = '';

  constructor() {
    this.lireQueryParams();

    // Fallback parc global si aucun parc dans l'URL
    effect(() => {
      const parcGlobal = this.parcs().find((p) => p.global);
      if (parcGlobal && this.parcIdSelectionne() === null) {
        this.parcIdSelectionne.set(parcGlobal.id);
      }
    });

    // Synchronise l'URL à chaque changement de formulaire
    effect(() => {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          parcId: this.parcIdSelectionne(),
          referent: this.typeReferent(),
          mode: this.mode(),
          date: fmtDate(this.dateFin()),
        },
        replaceUrl: true,
      });
    });
  }

  ngOnDestroy() {
    this.chartRoot?.dispose();
  }

  private lireQueryParams() {
    const p = this.route.snapshot.queryParams;

    const parcId = parseInt(p['parcId'], 10);
    if (!isNaN(parcId)) this.parcIdSelectionne.set(parcId);

    if (REFERENTS_VALIDES.includes(p['referent'])) this.typeReferent.set(p['referent']);

    if (MODES_VALIDES.includes(p['mode'])) this.mode.set(p['mode']);

    const date = p['date'] ? new Date(p['date']) : null;
    if (date && !isNaN(date.getTime())) this.dateFin.set(date);
  }

  onPeriodeChange(e: PeriodeChangee) {
    this.mode.set(e.mode);
    this.dateFin.set(e.dateFin);
  }

  onChartReady(host: HTMLDivElement) {
    this.chartHost = host;
    if (this.chartData.length > 0) this.initialiserGraphique();
  }

  async chargerDonnees() {
    const parcId = this.parcIdSelectionne();
    if (parcId === null) return;

    this.chargement.set(true);
    this.erreur.set(null);
    this.chargementGraphique.set(true);

    try {
      const [res, installations] = await Promise.all([
        this.parcService.getReferentsParc(parcId, this.mode(), this.dateFin(), this.typeReferent()),
        this.installationService.listeInstallations(parcId),
      ]);
      const nomParId = new Map<number, string>((installations ?? []).map((i) => [i.id, i.nom]));
      this.seriesLabel = res?.champs?.val?.libelle ?? this.typeReferent();
      this.chartData = (res?.datas ?? [])
        .map((p) => ({ date: Date.parse(p.date), val: Number(p.val) }))
        .filter((p) => Number.isFinite(p.date));
      this.extraireTableau(res?.sources?.[this.typeReferent()] ?? [], nomParId, res?.datas ?? []);
      this.plageSelectionnee.set(null);
      this.afficherGraphique.set(true);
      this.initialiserGraphique();
    } catch (e: any) {
      this.erreur.set(e?.message ?? 'Erreur inconnue');
      this.chargementGraphique.set(false);
    } finally {
      this.chargement.set(false);
    }
  }

  fmt(v: number | null, decimales = 1): string {
    if (v === null) return '—';
    return v.toLocaleString('fr-FR', {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
  }

  trierPar(colonne: number | 'source') {
    const current = this.sortEtat();
    if (!current || current.colonne !== colonne) {
      this.sortEtat.set({ colonne, direction: 'asc' });
    } else if (current.direction === 'asc') {
      this.sortEtat.set({ colonne, direction: 'desc' });
    } else {
      this.sortEtat.set(null);
    }
  }

  iconeSort(colonne: number | 'source'): string {
    const s = this.sortEtat();
    if (!s || s.colonne !== colonne) return 'unfold_more';
    return s.direction === 'asc' ? 'arrow_upward' : 'arrow_downward';
  }

  private extraireTableau(
    entries: SourceEntry[],
    nomParId: Map<number, string> = new Map(),
    datas: { date: string; val: number }[] = [],
  ) {
    if (!entries.length) {
      this.tableauColonnes.set([]);
      this.tableauLignes.set([]);
      this.ligneRef.set([]);
      this.ligneSomme.set([]);
      return;
    }
    const ids = Object.keys(entries[0])
      .filter((k) => k !== 'date')
      .sort((a, b) => Number(a) - Number(b));

    this.tableauColonnes.set(
      entries.map((e) => {
        const d = new Date(e.date);
        const label = isNaN(d.getTime())
          ? e.date
          : d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        return { label, ts: d.getTime() };
      }),
    );

    this.tableauLignes.set(
      ids.map((id) => {
        const nom = nomParId.get(Number(id));
        return {
          id,
          label: nom ? `${nom} (${id})` : id,
          valeurs: entries.map((e) => {
            const v = parseFloat(e[id]);
            return isNaN(v) ? null : v;
          }),
        };
      }),
    );

    this.ligneRef.set(
      entries.map((_, i) => {
        const v = datas[i]?.val;
        return v != null && Number.isFinite(v) ? v : null;
      }),
    );

    this.ligneSomme.set(
      entries.map(
        (e) =>
          ids.reduce((sum, id) => {
            const v = parseFloat(e[id]);
            return sum + (isNaN(v) ? 0 : v);
          }, 0) / 1_000_000,
      ),
    );
  }

  private initialiserGraphique() {
    const host = this.chartHost;
    if (!host || !isPlatformBrowser(this.platformId)) {
      this.chargementGraphique.set(false);
      return;
    }
    this.chartRoot?.dispose();
    this.chartRoot = createReferentChart(host, this.chartData, this.seriesLabel, (debut, fin) => {
      this.plageSelectionnee.set({ debut: debut.getTime(), fin: fin.getTime() });
    });
    setTimeout(() => this.chargementGraphique.set(false), 0);
  }
}
